import { inject, Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import { AUTH_STORE_TOKEN, IStore } from '../store/interface';
import { AuthReferrer, ITokenModel, ITokenService } from './interface';
import { API_CONFIG } from '../config';

export function SERVICE_TOKEN_FACTORY(): ITokenService {
  return new TokenService(inject(AUTH_STORE_TOKEN));
}

@Injectable()
export class TokenService implements ITokenService, OnDestroy {
  private refresh$ = new Subject<ITokenModel>();
  private change$ = new BehaviorSubject<ITokenModel | null>(null);
  private interval$: Subscription = new Subscription;
  private _referrer: AuthReferrer = {};

  constructor(
    @Inject(AUTH_STORE_TOKEN) private store: IStore
  ) {
  }

  /**
   * トークンリフレッシュのオブザーバーパターン
   */
  get refresh(): Observable<ITokenModel> {
    this.builderRefresh();
    return this.refresh$.pipe(share());
  }

  /**
   * アクセス権限がないときの遷移先を取得する
   */
  get login_url(): string {
    return API_CONFIG.login_url;
  }

  /**
   * リクエスト元のURLを取得する
   */
  get referrer(): AuthReferrer {
    return this._referrer;
  }

  /**
   * LocalStorage中のトークン情報を更新する
   */
  set(data: ITokenModel): boolean {
    this.change$.next(data);
    return this.store.set(API_CONFIG.store_key, data);
  }

  /**
   * LocalStorage中のトークン情報を取得する
   */
  get<T extends ITokenModel>(): T {
    const data = this.store.get(API_CONFIG.store_key);
    return data as T;
  }

  /**
   * LocalStorage中のトークン情報をクリアする
   */
  clear(options: { onlyToken: boolean } = { onlyToken: false }): void {
    let data: ITokenModel | null = null;
    this.store.remove(API_CONFIG.store_key);
    this.change$.next(data);
  }

  /**
   * トークンが更新たことのオブザーバーパターン
   */
  change(): Observable<ITokenModel | null> {
    return this.change$.pipe(share());
  }

  /**
   * トークンリフレッシュのタイマーを生成する
   */
  private builderRefresh(): void {
    const model = this.get() as ITokenModel;
    this.cleanRefresh();
    this.interval$ = interval(model.expiresIn)
      .pipe(
        map(() => {

          const expired = model.expiresIn || 0;
          if (expired <= 0) {
            return null;
          }

          const curTime = new Date().valueOf() + 1000;
          return expired <= curTime ? model : null;
        }),
        filter(v => v != null),
      )
      .subscribe(res => this.refresh$.next(res!));
  }

  /**
   * トークンリフレッシュのタイマーを削除する
   */
  private cleanRefresh(): void {
    if (this.interval$ && !this.interval$.closed) {
      this.interval$.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.cleanRefresh();
  }

}
